/**
 * Navbar button helpers.
 *
 * The navbar's buttons are stored as an ordered, reorderable `ctaButtons` array
 * (same shape as Hero/TextButton CTA buttons). Pre-2026-06 pages instead carry a
 * single legacy `ctaText`/`ctaLink` pair; `getNavbarCtaButtons` normalizes both
 * into one list so the renderer and editor never branch on storage shape.
 */

import { getButtonTextColor, isValidHexColor } from "@/features/landing-pages/_logic/color-utils";
import type { CtaButton, NavbarButtonColorMode, NavbarComponent } from "../../_types";

/** Stable id used when synthesizing a button from the legacy `ctaText`/`ctaLink` pair. */
export const LEGACY_NAVBAR_CTA_ID = "legacy-cta";

/** Fields `getNavbarCtaButtons` reads — accepts a full navbar or just the relevant slice. */
type NavbarButtonSource = Pick<NavbarComponent, "ctaButtons" | "ctaText" | "ctaLink">;

/**
 * Resolves the navbar's buttons, preferring the modern `ctaButtons` array and
 * falling back to the legacy single `ctaText`/`ctaLink` pair for older pages.
 *
 * Once `ctaButtons` is set (even to an empty array) it is authoritative — an empty
 * array means "no buttons", not "fall back to legacy".
 *
 * @param navbar - The navbar component (or its button-related fields).
 * @returns The ordered list of buttons to render/edit. Empty when none are configured.
 */
export function getNavbarCtaButtons(navbar: NavbarButtonSource): CtaButton[] {
  if (navbar.ctaButtons) return navbar.ctaButtons;
  if (navbar.ctaText) {
    return [{ id: LEGACY_NAVBAR_CTA_ID, label: navbar.ctaText, url: navbar.ctaLink ?? "" }];
  }
  return [];
}

/**
 * Resolves the navbar's section-wide button background + contrast-aware text color from its
 * color mode. "custom" uses the (validated) custom hex; anything else falls back to the site
 * primary color. Individual buttons may still override this via their own `color` field.
 *
 * @param colorMode - The navbar's button color mode ("primary" or "custom").
 * @param customColor - The navbar's custom button color (hex), used when `colorMode` is "custom".
 * @param primaryColor - The page's primary brand color (hex), used as the fallback.
 * @returns The resolved background color and its contrast-aware text color.
 */
export function resolveNavButtonColors(
  colorMode: NavbarButtonColorMode | undefined,
  customColor: string | undefined,
  primaryColor: string
): { bgColor: string; textColor: string } {
  const safeCustom = customColor && isValidHexColor(customColor) ? customColor : primaryColor;
  const bgColor = colorMode === "custom" ? safeCustom : primaryColor;
  return { bgColor, textColor: getButtonTextColor(bgColor) };
}
